import { useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import type { AdminLegacySettlementCalendarQuery } from "../../../../__generated__/graphql";
import {
  formatDateToYearMonth,
  formatDateToYearMonthDay,
  formatDateToYearMonthDayDayOfWeek,
} from "../../../../utils/date/format-date";

type CalendarDay = AdminLegacySettlementCalendarQuery["adminLegacySettlementCalendar"][number];

const calendarStyles = `
  .legacy-settlement-calendar .react-calendar {
    width: 100% !important;
    border: none !important;
    font-size: 13px !important;
  }

  .legacy-settlement-calendar .react-calendar__navigation button {
    min-width: 36px !important;
    background: transparent !important;
  }

  .legacy-settlement-calendar .react-calendar__tile {
    text-align: left !important;
    padding: 0.35rem 0.25rem !important;
    min-height: 72px !important;
  }

  .legacy-settlement-calendar .react-calendar__tile--active {
    background: #eff6ff !important;
    color: inherit !important;
    border-radius: 0.5rem !important;
  }

  .legacy-settlement-calendar .react-calendar__tile--now {
    background: #f3f4f6 !important;
    color: inherit !important;
    border-radius: 0.5rem !important;
  }

  .legacy-settlement-calendar .react-calendar__month-view__weekdays__weekday {
    text-transform: none !important;
    font-size: 12px !important;
    color: #6b7280 !important;
  }
`;

function formatCompactAmount(amount: number): string {
  return amount.toLocaleString("ko-KR");
}

function formatSoldAt(soldAt: string): string {
  return new Date(soldAt).toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

interface LegacySettlementCalendarProps {
  yearMonth: string;
  days: CalendarDay[];
  loading: boolean;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onChangeYearMonth: (yearMonth: string, nextSelectedDate: Date) => void;
}

export default function LegacySettlementCalendar({
  yearMonth,
  days,
  loading,
  selectedDate,
  onSelectDate,
  onChangeYearMonth,
}: LegacySettlementCalendarProps) {
  const dayMap = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days]
  );
  const selectedDay = dayMap.get(formatDateToYearMonthDay(selectedDate, "-"));

  return (
    <div className="space-y-4">
      <style>{calendarStyles}</style>

      <div className="rounded-lg border border-gray-200 bg-white p-4 legacy-settlement-calendar">
        <div className="mb-3">
          <div className="font-semibold text-gray-900">레거시 정산 캘린더</div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1 text-rose-600">
              <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
              레거시 합산 금액
            </div>
            <div className="flex items-center gap-1 text-gray-500">
              <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
              4/1 이후 참고 금액
            </div>
          </div>
        </div>

        <Calendar
          value={selectedDate}
          onChange={(value) => onSelectDate(value as Date)}
          calendarType="gregory"
          className="w-full"
          activeStartDate={new Date(`${yearMonth}-01T00:00:00`)}
          onActiveStartDateChange={({ activeStartDate }) => {
            if (!activeStartDate) {
              return;
            }

            onChangeYearMonth(
              formatDateToYearMonth(activeStartDate, "-"),
              activeStartDate
            );
          }}
          tileContent={({ date, view }) => {
            if (view !== "month") {
              return null;
            }

            const day = dayMap.get(formatDateToYearMonthDay(date, "-"));

            return (
              <div className="mt-1 min-h-[32px] rounded bg-gray-50 px-1 py-1 leading-tight">
                {day?.legacyEligibleAmount ? (
                  <div className="text-[10px] font-semibold text-rose-500">
                    {loading ? "-" : formatCompactAmount(day.legacyEligibleAmount)}
                  </div>
                ) : null}
                {day?.postCutoffReferenceAmount ? (
                  <div className="text-[10px] font-semibold text-gray-400">
                    {loading ? "-" : formatCompactAmount(day.postCutoffReferenceAmount)}
                  </div>
                ) : null}
              </div>
            );
          }}
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
        <div className="mb-3 font-semibold text-gray-900">
          {formatDateToYearMonthDayDayOfWeek(selectedDate)}
        </div>

        {!selectedDay ? (
          <div className="text-gray-500">해당 날짜에 표시할 레거시/참고 금액이 없습니다.</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md bg-rose-50 p-3">
              <div className="mb-1 text-sm font-semibold text-rose-700">
                레거시 합산 금액
              </div>
              <div className="text-lg font-bold text-rose-600">
                {formatCompactAmount(selectedDay.legacyEligibleAmount)}원
              </div>
              <div className="mt-2 space-y-2">
                {selectedDay.legacyEligibleSales.length === 0 ? (
                  <div className="text-xs text-rose-400">해당 없음</div>
                ) : (
                  selectedDay.legacyEligibleSales.map((sale) => (
                    <div
                      key={`legacy-${sale.id}`}
                      className="rounded border border-rose-100 bg-white px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-gray-900">{sale.name}</div>
                        <div className="whitespace-nowrap text-xs text-gray-500">
                          {formatSoldAt(sale.soldAt)}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        정가 {formatCompactAmount(sale.price)}원 / 정산금액{" "}
                        {formatCompactAmount(sale.sellerAmountWithFee)}원
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-md bg-gray-50 p-3">
              <div className="mb-1 text-sm font-semibold text-gray-600">
                4/1 이후 참고 금액
              </div>
              <div className="text-lg font-bold text-gray-500">
                {formatCompactAmount(selectedDay.postCutoffReferenceAmount)}원
              </div>
              <div className="mt-2 space-y-2">
                {selectedDay.postCutoffReferenceSales.length === 0 ? (
                  <div className="text-xs text-gray-400">해당 없음</div>
                ) : (
                  selectedDay.postCutoffReferenceSales.map((sale) => (
                    <div
                      key={`reference-${sale.id}`}
                      className="rounded border border-gray-200 bg-white px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-gray-900">{sale.name}</div>
                        <div className="whitespace-nowrap text-xs text-gray-500">
                          {formatSoldAt(sale.soldAt)}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        정가 {formatCompactAmount(sale.price)}원 / 참고금액{" "}
                        {formatCompactAmount(sale.sellerAmountWithFee)}원
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
